import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";

import { useTranslation } from "react-i18next";
import { Link, useRouteError } from "react-router";

import { DSTLogo } from "@/components/dst-logo";
import { Button } from "@/components/ui/button";

const ErrorScreen = ({
  error,
  onRetry,
  showRetry,
}: {
  error?: Error;
  onRetry?: () => void;
  showRetry?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <section className="flex h-screen flex-col items-center justify-center gap-8 text-center">
      <DSTLogo className="h-24" />
      <div className="space-y-4">
        <p className="text-lg text-muted-foreground uppercase tracking-[0.35em]">
          404
        </p>
        <h1 className="text-balance font-semibold text-3xl text-primary">
          {t("errors.generic")}
        </h1>
        <p className="text-pretty text-base text-muted-foreground">
          {t("errors.genericDescription")}
        </p>
      </div>

      {import.meta.env.DEV && error && (
        <pre className="max-w-3xl overflow-x-auto rounded-md bg-muted/60 p-6 text-left text-sm">
          {error.stack ?? error.message}
        </pre>
      )}

      <div className="flex flex-col gap-4 sm:flex-row">
        {showRetry && onRetry ? (
          <Button onClick={onRetry} size="lg">
            {t("common.tryAgain", "Try Again")}
          </Button>
        ) : null}
        <Button asChild variant={showRetry ? "outline" : "default"}>
          <Link aria-label="Go home" to="/">
            {t("labels.return-to-homepage")}
          </Link>
        </Button>
      </div>
    </section>
  );
};

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  private renderFallback(): ReactNode {
    return (
      <ErrorScreen
        error={this.state.error}
        onRetry={this.handleReset}
        showRetry
      />
    );
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    // Integrate with an error reporting service here if required.
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

export const RouterErrorFallback = (): ReactNode => {
  const error = useRouteError();

  return <ErrorScreen error={error instanceof Error ? error : undefined} />;
};
